import { useMemo, useState, useCallback } from 'react';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Option } from '../../core/components/Fields/AutoCompleteField';
import { useAssistanceContext } from '../pages/Assistance/AssistanceContext';
import { useAssistanceFieldContext } from '../pages/Assistance/AssistanceFieldContext';
import { useSafeFeatureFlag } from '../../core/utils/hooks/useFeatureFlag';
import { useApplicationContext } from '../../core/contexts/ApplicationContext';
import { MASTERDATA_BROWSER_KEY } from './useLookup';

interface ExtractionSearchResult {
    articleId: string;
    description: string;
}

interface ExtractionSearchResults {
    timestamp: string;
    results: ExtractionSearchResult[];
}

/**
 * Hook that provides extraction search results as dropdown options for the article description field.
 * When the feature flag is enabled and stored extraction search results exist on the line item,
 * these are shown immediately on focus instead of firing a live search.
 *
 * Returns:
 * - `extractionOptions`: The stored results as Option[], with a section header
 * - `isEnabled`: Whether the feature is active (flag enabled + results exist)
 * - `shouldShowStored`: Whether to show stored results (true on initial focus, false when user types)
 * - `onInputChange`: Call when the user types to switch to live search
 * - `onFocus`: Call on focus to reset to stored results view
 */
export const useExtractionSearchResults = ({
    fieldName,
    lookupFieldName,
    value,
}: {
    fieldName: string;
    lookupFieldName: string;
    value: string;
}) => {
    const { record, document } = useAssistanceContext();
    const { itemIndex, itemType } = useAssistanceFieldContext();
    const { user } = useApplicationContext();
    const { t } = useTranslation('masterdata');

    const channelId = record?.channel?.id;
    const customerShortName = user?.customer?.shortName;

    const { value: isFeatureFlagEnabled } = useSafeFeatureFlag('showExtractionSearchResultsInAssistance', false, {
        channelId,
        customerShortName,
    });

    // Access the line item's extractionSearchResults from the document data
    const extractionSearchResults: ExtractionSearchResults | null = useMemo(() => {
        if (!isFeatureFlagEnabled || !document || itemIndex == null || !itemType) return null;
        const items = document[camelCase(itemType)];
        if (!items || !items[itemIndex]) return null;
        return items[itemIndex].extractionSearchResults || null;
    }, [isFeatureFlagEnabled, document, itemIndex, itemType]);

    const isDescriptionField = fieldName === 'article_description' || fieldName === 'description';
    const isEnabled = isFeatureFlagEnabled && isDescriptionField && !!extractionSearchResults?.results?.length;

    // Track whether we should show stored results vs live search
    const [showStored, setShowStored] = useState(true);

    // Convert stored results to Option format
    const extractionOptions: Option[] = useMemo(() => {
        if (!isEnabled || !extractionSearchResults?.results?.length) return [];

        const header: Option = {
            role: 'message',
            label: 'Top matches based on document',
            value: '_extraction_search_header',
        };

        const resultOptions: Option[] = extractionSearchResults.results.map((result) => ({
            label: result.description,
            value: result.description,
            // Include fields that renderArticleIdOption expects for display
            [camelCase(lookupFieldName)]: result.description,
            articleId1: result.articleId,
            description: result.description,
            // Marks the commit payload (`_lookupEntity`) as an extraction search selection, so the
            // backend resolves the article by its number and fills the line item's master data
            // (stored results carry no masterdata entity id, i.e. no `_lookup_id`)
            _extractionSearchResult: true,
        }));

        const separator: Option = { role: 'separator', label: '', value: '_extraction_search_separator' };
        const browserButton: Option = {
            role: 'button',
            label: t('browser.openBrowser'),
            value: MASTERDATA_BROWSER_KEY,
        };

        return [header, ...resultOptions, separator, browserButton];
    }, [isEnabled, extractionSearchResults, lookupFieldName]);

    const handleInputChange = useCallback(
        (newValue: string) => {
            if (newValue !== value && newValue !== '') {
                // User typed something different from the current value -- switch to live search
                setShowStored(false);
                return;
            }
            // User cleared the field or typed back the original value -- show stored results again
            setShowStored(true);
        },
        [value]
    );

    const handleFocus = useCallback(() => {
        // On focus, show stored results if available
        setShowStored(true);
    }, []);

    return {
        extractionOptions,
        extractionSearchResults,
        isEnabled,
        shouldShowStored: isEnabled && showStored,
        onInputChange: handleInputChange,
        onFocus: handleFocus,
    };
};
